import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { authLog } from "@/lib/auth-log";
import { getAuthTraceText } from "@/lib/auth-trace";
import { getOAuthErrorFromQuery, isAwaitingSupabaseUrlSession, hasOauthReturnParams } from "@/lib/oauth-url";
import {
  ApiError,
  authServiceUnavailableRetryAfterSeconds,
  isAuthServiceUnavailablePayload,
} from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import {
  shouldKeepHubShellForDevRestart,
  shouldSuppressFullPageMeLoading,
} from "@/lib/protected-dev-restart";
import { useDevStackRestartFlags } from "@/lib/useDevStackRestartFlags";
import { WikiOAuthBootstrap } from "./WikiOAuthBootstrap";
import { LaborLockdownGate } from "./LaborLockdownGate";
import { LaborPageDepartureWatch } from "./LaborPageDepartureWatch";
import { LaborSpaNavigationWatch } from "./LaborSpaNavigationWatch";

const OAUTH_WAIT_MS = 60_000;

export function Protected() {
  const { session, loading: sessionLoading } = useAuth();
  const location = useLocation();
  const search = location.search;
  const urlOAuthError = getOAuthErrorFromQuery(search);
  const awaitingUrlSession = isAwaitingSupabaseUrlSession(location.search, location.hash);
  const oauthInUrl = hasOauthReturnParams(location.search, location.hash);
  const [oauthWaitExpired, setOauthWaitExpired] = useState(false);

  useEffect(() => {
    if (!awaitingUrlSession) {
      setOauthWaitExpired(false);
      return;
    }
    setOauthWaitExpired(false);
    const timer = window.setTimeout(() => setOauthWaitExpired(true), OAUTH_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [awaitingUrlSession]);

  const meQuery = useIntegratorMe();
  const devRestartFlags = useDevStackRestartFlags();
  const hasCachedSession = Boolean(session);
  const keepShellForDevRestart = shouldKeepHubShellForDevRestart({
    dev: import.meta.env.DEV,
    hasCachedSession,
    hasMeData: meQuery.data != null,
    meError: meQuery.error,
    devRestartFlags,
  });

  const lastSnapRef = useRef<string>("");
  useEffect(() => {
    const snap = JSON.stringify({
      path: location.pathname,
      hasSearch: search.length > 0,
      sessionLoading,
      hasSession: Boolean(session),
      userId: session?.user.id ?? null,
      awaitingUrlSession,
      urlOAuthError: urlOAuthError?.error,
      oauthInUrl,
      oauthWaitExpired,
      meStatus: meQuery.status,
      meFetch: meQuery.fetchStatus,
      meErr:
        meQuery.error instanceof ApiError
          ? meQuery.error.status
          : meQuery.error
            ? "other"
            : null,
    });
    if (snap !== lastSnapRef.current) {
      lastSnapRef.current = snap;
      authLog("protected:state", JSON.parse(snap) as Record<string, unknown>);
    }
  });

  if (sessionLoading) {
    return <FullPageStatus title="Loading…" />;
  }

  if (!session) {
    if (urlOAuthError) {
      return (
        <OauthErrorScreen
          errorCode={urlOAuthError.error}
          errorDescription={urlOAuthError.errorDescription}
        />
      );
    }
    if (awaitingUrlSession && !oauthWaitExpired) {
      return (
        <FullPageStatus
          title="Signing you in…"
          message="Finishing sign-in with Supabase. This page will load in a few seconds."
        />
      );
    }
    return <Navigate to="/sign-in" replace />;
  }

  // Initial /me load only — background refetch must not unmount the hub (for example when
  // OpenIncidentForm mounts inside a modal and staleTime has expired).
  if (meQuery.isPending) {
    if (
      shouldSuppressFullPageMeLoading({
        dev: import.meta.env.DEV,
        mePending: true,
        hasCachedSession,
        devRestartFlags,
      })
    ) {
      return <ProtectedHubShell />;
    }
    return <FullPageStatus title="Loading…" />;
  }

  if (keepShellForDevRestart) {
    return <ProtectedHubShell />;
  }

  if (meQuery.error) {
    const err = meQuery.error;
    if (err instanceof ApiError && err.status === 401) {
      return <MeApiRejected401 error={err} />;
    }
    if (err instanceof ApiError && isAuthServiceUnavailablePayload(err.status, err.body)) {
      return (
        <MeAuthServiceUnavailable
          error={err}
          onRetry={() => {
            void meQuery.refetch();
          }}
        />
      );
    }
    const status = err instanceof ApiError ? err.status : undefined;
    if (status === undefined) {
      return (
        <FullPageStatus
          title="Could not reach the API"
          message={err instanceof Error ? err.message : "Check that the dev server is proxying to the API, then reload."}
        />
      );
    }
    const title =
      status >= 500 ? "Server error" : status === 403 ? "Access denied" : "Request failed";
    return <FullPageStatus title={title} message={err.message} />;
  }

  return <ProtectedHubShell />;
}

function ProtectedHubShell() {
  return (
    <>
      <WikiOAuthBootstrap />
      <LaborSpaNavigationWatch />
      <LaborPageDepartureWatch />
      <LaborLockdownGate>
        <Outlet />
      </LaborLockdownGate>
    </>
  );
}

function OauthErrorScreen({ errorCode, errorDescription }: { errorCode: string; errorDescription: string }) {
  const logged = useRef(false);
  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    authLog("protected:oauthErrorFromIdp", {
      error: errorCode,
      descriptionLen: errorDescription.length,
    });
  }, [errorCode, errorDescription]);

  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-amber-950">Sign-in did not complete</h1>
        <p className="mt-1 font-mono text-sm text-amber-900">
          {errorCode}
        </p>
        {errorDescription ? (
          <p className="mt-2 text-sm text-amber-900/90">
            {errorDescription}
          </p>
        ) : null}
        <p className="mt-3 text-sm text-amber-900/80">
          The identity provider (for example Google) or Supabase returned an error. Try again, or use a different account if you were not allowed to sign in.
        </p>
        <Link
          to="/sign-in"
          replace
          className="mt-4 inline-block rounded-md bg-amber-900 px-4 py-2 text-sm font-medium text-amber-50"
        >
          Back to sign in
        </Link>
      </div>
    </div>
  );
}

function apiErrorDetail(error: ApiError): string | undefined {
  const { body } = error;
  if (body && typeof body === "object" && "detail" in body) {
    return String((body as { detail: unknown }).detail);
  }
  return undefined;
}

function meVerificationSupportBundle(error: ApiError, user: { id: string; email?: string | null } | null): string {
  const lines: string[] = [
    "Integrator — sign-in verification failed",
    `Generated: ${new Date().toISOString()}`,
    `Page: ${typeof window !== "undefined" ? window.location.href : ""}`,
  ];
  if (user) {
    lines.push(`User id: ${user.id}`);
    if (user.email) lines.push(`Email: ${user.email}`);
  }
  lines.push("", "--- Server response (sign-in check) ---", `HTTP ${error.status}`, error.message);
  if (error.body !== undefined && error.body !== null) {
    lines.push(
      typeof error.body === "object" ? JSON.stringify(error.body, null, 2) : String(error.body),
    );
  }
  lines.push("", "--- Auth trace (this browser tab only, not sent automatically) ---", getAuthTraceText().trimEnd());
  lines.push(
    "",
    "--- For operators / local development ---",
    "The application server checks your Supabase access token. It needs SUPABASE_URL and the publishable Supabase API key in the repository root .env as SUPABASE_PUBLISHABLE_KEY (or the same value as VITE_SUPABASE_ANON_KEY). Use the key from Supabase Dashboard → Project Settings → API → Project API keys (anon / public), not the JWT signing secret. The API process reads those variables at startup, so credential changes apply only after the process that loads this repository root .env is replaced.",
  );
  return lines.join("\n");
}

/** Server rejected the Supabase access token. Browser session may still be valid; do not auto sign-out (user can copy details). */
function MeApiRejected401({ error }: { error: ApiError }) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const detail = apiErrorDetail(error);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const logged = useRef(false);
  useEffect(() => {
    if (logged.current) return;
    logged.current = true;
    authLog("ui:apiMe401:screen", {
      message: error.message,
      detail,
      traceLines: getAuthTraceText().split("\n").length,
    });
  }, [error, detail]);

  const copySupportBundle = () => {
    void (async () => {
      const text = meVerificationSupportBundle(error, session?.user ?? null);
      try {
        await navigator.clipboard.writeText(text);
        setCopyState("copied");
        authLog("ui:apiMe401:copySupportBundle", { length: text.length });
        window.setTimeout(() => setCopyState("idle"), 2500);
      } catch {
        setCopyState("failed");
      }
    })();
  };

  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="max-w-lg rounded-lg border border-rose-200 bg-rose-50/80 p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-rose-950">We could not finish signing you in</h1>
        <p className="mt-2 text-sm leading-relaxed text-rose-900/90">
          This often happens when your sign-in to the application has expired, for example after time away over a
          weekend. The browser can still look signed in while the application no longer accepts that older session.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-rose-900/90">
          <strong className="font-semibold text-rose-950">What to do:</strong> sign out below, then sign in again. That
          replaces the expired session with a fresh one. Reloading the page alone usually does not fix this.
        </p>
        <button
          type="button"
          className="mt-4 w-full rounded-md bg-rose-900 px-4 py-2.5 text-sm font-medium text-rose-50 hover:bg-rose-800 sm:w-auto"
          onClick={() => {
            void (async () => {
              await signOut();
              authLog("ui:apiMe401:signOut", {});
              navigate("/sign-in", { replace: true });
            })();
          }}
        >
          Sign out, then sign in again
        </button>
        <p className="mt-4 text-sm text-rose-900/80">
          If you still see this after signing in again, use Copy error details and send that text to support. Nothing
          is uploaded automatically; copying is the only way it leaves your computer.
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-950 shadow-sm hover:bg-rose-100"
            onClick={copySupportBundle}
          >
            {copyState === "copied" ? "Copied" : "Copy error details"}
          </button>
          {copyState === "failed" ? (
            <span className="text-sm text-rose-800">
              Your browser blocked the clipboard. You can still open Technical details and type or screenshot what you
              see, or ask support for another way to send logs.
            </span>
          ) : null}
        </div>
        <details className="mt-4 rounded-md border border-rose-200/80 bg-white/60 text-sm text-rose-900/90">
          <summary className="cursor-pointer select-none px-3 py-2 font-medium text-rose-950 outline-none marker:text-rose-700">
            Technical details
          </summary>
          <div className="border-t border-rose-200/80 px-3 py-2 text-xs text-rose-800/90">
            <p>
              The server rejected the sign-in check (<span className="font-mono">/api/me</span>, not authorized). Your
              browser may still hold an identity session while the application server refuses it.
            </p>
            {detail ? (
              <pre className="mt-2 max-h-40 overflow-auto rounded border border-rose-100 bg-white/90 p-2 font-mono text-[11px] text-rose-900">
                {detail}
              </pre>
            ) : null}
          </div>
        </details>
      </div>
    </div>
  );
}

/**
 * The hub API is up but could not reach Supabase Auth to verify the session
 * (503 `auth_service_unavailable`). The session is almost certainly fine —
 * show a calm countdown and retry automatically instead of the scary
 * "sign out and back in" screen (which is for real 401 rejections only).
 */
function MeAuthServiceUnavailable({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  const retryAfterSeconds = authServiceUnavailableRetryAfterSeconds(error.body);
  const [secondsLeft, setSecondsLeft] = useState(retryAfterSeconds);
  const [attempt, setAttempt] = useState(1);
  const detail = apiErrorDetail(error);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          setAttempt((n) => n + 1);
          onRetry();
          return retryAfterSeconds;
        }
        return current - 1;
      });
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [onRetry, retryAfterSeconds]);

  return (
    <div className="flex h-screen items-center justify-center p-4">
      <div className="max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-amber-950">
          The hub is up, but sign-in verification is temporarily unavailable
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-amber-900/90">
          The hub could not reach its sign-in service to confirm your session. This is a temporary
          outage on the connection to the sign-in service — your session is almost certainly fine,
          and you do <strong className="font-semibold">not</strong> need to sign out.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-amber-900/90" aria-live="polite">
          Retrying automatically in <span className="font-semibold tabular-nums">{secondsLeft}s</span>
          {attempt > 1 ? ` (attempt ${attempt})` : ""}. This usually clears within a minute or two.
        </p>
        <button
          type="button"
          className="mt-4 rounded-md bg-amber-900 px-4 py-2 text-sm font-medium text-amber-50 hover:bg-amber-800"
          onClick={() => {
            setAttempt((n) => n + 1);
            setSecondsLeft(retryAfterSeconds);
            onRetry();
          }}
        >
          Retry now
        </button>
        <p className="mt-4 text-sm text-amber-900/80">
          If this screen stays for more than a few minutes, report it to support and mention the time
          it started and the technical detail below.
        </p>
        {detail ? (
          <p className="mt-2 rounded border border-amber-200/80 bg-white/70 px-2 py-1 font-mono text-[11px] text-amber-900">
            {detail}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function FullPageStatus({ title, message }: { title: string; message?: string }) {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-slate-900">{title}</h1>
        {message ? <p className="mt-2 text-sm text-slate-600">{message}</p> : null}
      </div>
    </div>
  );
}
